import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { useContext, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { Typography } from '@/shared/ui';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { getPublicRouteUrl } from '@/helpers/route';

import AuthContext from '@/contexts/Auth/AuthContext';

import { SERVICE_CODES } from '@/lib/consts';

import type { CmsSlide } from '@/api/types';

import { HeroInfoSliderSlide } from './HeroInfoSliderSlide';

import './HeroInfoSliderStyles.scss';

type PlaceholderSlide = Pick<CmsSlide, 'header' | 'img' | 'text_color'> & { link: string };

const PLACEHOLDER_SLIDE: PlaceholderSlide = {
  header: 'Замовляйте доставку витягу про несудимість зокрема з апостилем',

  link: ROUTES_BY_SERVICE_CODE[SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT],
  img: {
    desktop: '/images/slider/extended_criminal_record_extract.webp',
    mobile: '/images/slider/extended_criminal_record_extract-m.webp',
  },
  text_color: 'black',
};
type SlideNavigation = {
  resolvedLink?: string;
  target: '_self' | '_blank';
  service_code?: CmsSlide['service_code'];
};

type Props = {
  slides: CmsSlide[];
  showPlaceholder?: boolean;
};

const getSlideNavigation = (slide: CmsSlide, isLoggedIn: boolean): SlideNavigation => {
  if (slide.link?.trim()) {
    return {
      resolvedLink: slide.external ? slide.link : getPublicRouteUrl(slide.link, isLoggedIn),
      target: slide.external ? '_blank' : '_self',
      service_code: slide.service_code ?? undefined,
    };
  }

  if (slide.service_code && ROUTES_BY_SERVICE_CODE[slide.service_code]) {
    return {
      resolvedLink: getPublicRouteUrl(ROUTES_BY_SERVICE_CODE[slide.service_code], isLoggedIn),
      target: '_self',
      service_code: slide.service_code,
    };
  }

  return {
    target: '_self',
    service_code: slide.service_code ?? undefined,
  };
};

export const HeroInfoSlider: React.FC<Props> = ({ slides, showPlaceholder = true }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext);

  const [isResizing, setIsResizing] = useState(false);
  const resizeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const onResize = (): void => {
      setIsResizing(true);

      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = window.setTimeout(() => {
        setIsResizing(false);
      }, 160);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);

      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const slideList = useMemo(() => {
    return slides.map((item) => ({
      ...item,
      ...getSlideNavigation(item, isLoggedIn),
    }));
  }, [isLoggedIn, slides]);

  return (
    <section>
      <Typography visuallyHidden as="h2">
        Перелік деяких можливостей Єдиного вікна для громадян
      </Typography>

      <div className={`hero-info-slider${isResizing ? ' hero-info-slider--resizing' : ''}`}>
        {slideList.length > 0 ? (
          <Swiper
            slidesPerView={1}
            className="swiper"
            navigation={false}
            pagination={{ enabled: false }}
            autoplay={{
              delay: 15000,
              disableOnInteraction: false,
            }}
            loop={slideList.length > 1}
            speed={1200}
            effect="fade"
            modules={[Navigation, Pagination, Autoplay]}
            allowTouchMove
          >
            {slideList.map(({ id, header, img, text, service_code, resolvedLink, target, text_color }) => (
              <SwiperSlide key={id}>
                <HeroInfoSliderSlide
                  header={header}
                  text={text}
                  img={img}
                  resolvedLink={resolvedLink}
                  target={target}
                  text_color={text_color}
                  serviceCode={service_code}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          showPlaceholder && (
            <HeroInfoSliderSlide
              header={PLACEHOLDER_SLIDE.header}
              img={PLACEHOLDER_SLIDE.img}
              resolvedLink={getPublicRouteUrl(PLACEHOLDER_SLIDE.link, isLoggedIn)}
              target="_self"
              text_color={PLACEHOLDER_SLIDE.text_color}
            />
          )
        )}
      </div>
    </section>
  );
};
